import { useState } from 'react';
import { avisoDePersonaRestaurada } from './aviso-de-persona-restaurada';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/lib/hooks/use-toast';
import { adminService } from '../services/admin-service';
import type { AdminUser } from '../types/admin.types';

/**
 * L5 (plan 335 · 1.3): las personas eliminadas, para restaurarlas.
 *
 * Eliminar libera el correo y saca a la persona de todos los listados; su historial queda. Desde
 * acá se la devuelve, con sus empresas y sus roles, si su correo sigue libre. Si ya lo tomó otra
 * cuenta, el servidor no la restaura (nunca se juntan cuentas) y el aviso lo dice.
 */
export function PersonasEliminadas() {
  const { toast } = useToast();
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [personas, setPersonas] = useState<AdminUser[] | null>(null);
  const [restaurando, setRestaurando] = useState<string | null>(null);

  async function cargar() {
    setCargando(true);
    try {
      const r = await adminService.getUsers({ status: 'deleted', limit: 100 });
      setPersonas(r.data);
    } catch {
      setPersonas([]);
    } finally {
      setCargando(false);
    }
  }

  function alternar() {
    const siguiente = !abierto;
    setAbierto(siguiente);
    if (siguiente && personas === null) void cargar();
  }

  async function restaurar(p: AdminUser) {
    setRestaurando(p.id);
    try {
      const r = await adminService.restaurarUsuario(p.id);
      toast({
        title: `${p.firstName} ${p.lastName} volvió a la plataforma.`,
        description: avisoDePersonaRestaurada(r.accesosApagados),
      });
      await cargar();
    } catch (e: any) {
      toast({
        variant: 'destructive',
        title: 'No se pudo restaurar',
        description: e?.response?.data?.error ?? 'Ocurrió un error.',
      });
    } finally {
      setRestaurando(null);
    }
  }

  return (
    <Card>
      <CardHeader className="p-0">
        <button
          type="button"
          onClick={alternar}
          className="flex w-full items-center gap-2 p-5 text-left"
          aria-expanded={abierto}
        >
          {abierto ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
          <CardTitle className="text-base">Personas eliminadas</CardTitle>
          <span className="text-sm text-muted-foreground">
            Su correo quedó libre. Se pueden restaurar si nadie lo tomó.
          </span>
        </button>
      </CardHeader>
      {abierto && (
        <CardContent>
          {cargando ? (
            <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Cargando" />
          ) : !personas || personas.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No hay personas eliminadas.
            </p>
          ) : (
            <ul className="divide-y">
              {personas.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <Link
                      to={`/plataforma/personas/${p.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {p.firstName} {p.lastName}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      {p.originalEmail ?? p.email} · {p.tenant?.name}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={restaurando === p.id}
                    onClick={() => restaurar(p)}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                    Restaurar
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
}
